import { createRoot } from "react-dom/client";
import { useEffect, useRef } from "react";
import { BrowserRouter, useSearchParams } from "react-router-dom";
import {
  DocumentEditorContainerComponent,
  Toolbar,
} from "@syncfusion/ej2-react-documenteditor";
import { registerLicense } from "@syncfusion/ej2-base";

import { TitleBar } from "./title-bar";
import "./index.css";

registerLicense(
  "NxYtGyMROh0gHDMgDk1jWE9FaF1LVmpAd1prRGFEfF9gdlZMZVVbQX9UQF1hT35adkZiUH1ccnxSRGNdWkdy"
);

// Hosted backend (the `wordeditor` ASP.NET Core app). The Document Editor
// container uses this base URL for server-side features (import, export,
// spell check, system clipboard, restrict editing).
const SERVICE_URL = "https://word-editor.capricontechnology.com/api/documenteditor/";
const REPORT_BASE_URL =
  "https://storage.googleapis.com/doc-app-static-site/reports/";

DocumentEditorContainerComponent.Inject(Toolbar);

const Default = () => {
  const [searchParams] = useSearchParams();
  const container = useRef(null);
  const titleBar = useRef(null);

  const fetchFile = async (fileUrl) => {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error(
        `Failed to fetch file: ${response.status} ${response.statusText}`
      );
    }
    const blob = await response.blob();
    return new File([blob], "downloaded.docx", { type: blob.type });
  };

  const handleFetchAndUpload = async (fileName, manual) => {
    const fileUrl =
      manual === "true"
        ? searchParams.get("file_url")
        : `${REPORT_BASE_URL}${fileName}`;

    try {
      const file = await fetchFile(fileUrl);

      const formData = new FormData();
      formData.append("File", file);

      const response = await fetch(`${SERVICE_URL}Import`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(
          `Upload failed: ${response.status} ${response.statusText}`
        );
      }

      // The Import endpoint returns the SFDT document as a plain string.
      return response.text();
    } catch (error) {
      console.error("Error fetching and uploading file:", error);
    }
  };

  const onLoadDefault = async () => {
    const file = searchParams.get("file");
    const manual = searchParams.get("manual");

    if (file && manual) {
      const defaultDocument = await handleFetchAndUpload(
        decodeURIComponent(file),
        manual
      );

      container.current.documentEditor.open(defaultDocument);
      container.current.documentEditor.documentName = decodeURIComponent(file);
      container.current.documentEditor.manual = manual;
      container.current.documentEditor.file_id = decodeURIComponent(
        searchParams.get("file_id") || ""
      );
    }

    titleBar.current.updateDocumentTitle();
    container.current.documentChange = () => {
      titleBar.current.updateDocumentTitle();
      container.current.documentEditor.focusIn();
    };
  };

  const renderComplete = () => {
    window.onbeforeunload = () => "Want to save your changes?";

    container.current.documentEditor.pageOutline = "#E0E0E0";
    container.current.documentEditor.acceptTab = true;
    container.current.documentEditor.resize();

    titleBar.current = new TitleBar(
      document.getElementById("documenteditor_titlebar"),
      container.current.documentEditor,
      true
    );

    onLoadDefault();
  };

  useEffect(() => {
    renderComplete();
  }, []);

  return (
    <div className="control-section">
      <div id="documenteditor_titlebar" className="e-de-ctn-title"></div>
      <div id="documenteditor_container_body">
        <DocumentEditorContainerComponent
          id="container"
          ref={container}
          style={{ display: "block" }}
          height="780px"
          serviceUrl={SERVICE_URL}
          enableToolbar
          locale="en-US"
        />
      </div>
    </div>
  );
};

const root = createRoot(document.getElementById("sample"));
root.render(
  <BrowserRouter>
    <Default />
  </BrowserRouter>
);
